// src/styles/theme.js
// Centralized theme configuration for consistent styling across the application

export const theme = {
  // Primary Colors
  primary: '#1a73e8',
  primaryHover: '#1557b0',
  primaryLight: '#e8f0fe',
  
  // Secondary Colors
  secondary: '#34a853',
  secondaryHover: '#2d8f47',
  secondaryLight: '#e6f4ea',
  
  // Accent Colors
  accent: '#fbbc05',
  accentHover: '#f9ab00',
  accentLight: '#fef7e0',
  
  // Status Colors
  success: '#34a853',
  warning: '#fbbc05',
  error: '#ea4335',
  info: '#1a73e8',
  
  // Background Colors
  background: '#f8f9fa',
  backgroundSecondary: '#ffffff',
  backgroundTertiary: '#f1f3f4',
  
  // Card Colors
  cardBackground: '#ffffff',
  cardBorder: '#dadce0',
  cardShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  
  // Text Colors
  text: '#202124',
  textSecondary: '#5f6368',
  textTertiary: '#9aa0a6',
  textInverse: '#ffffff',
  
  // Border Colors
  border: '#dadce0',
  borderLight: '#f1f3f4',
  borderDark: '#5f6368',
  
  // Spacing Scale
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    xxl: '3rem',      // 48px
  },
  
  // Border Radius
  borderRadius: {
    sm: '0.25rem',    // 4px
    md: '0.375rem',   // 6px
    lg: '0.5rem',     // 8px
    xl: '0.75rem',    // 12px
    full: '9999px',   // Fully rounded
  },
  
  // Typography
  typography: {
    fontFamily: {
      sans: ['Inter', 'system-ui', 'sans-serif'],
      mono: ['Fira Code', 'Monaco', 'monospace'],
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    lg: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
    xl: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
  },
  
  // Breakpoints (for responsive design)
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  // Z-index scale
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
};

// Theme variants for different contexts
export const themeVariants = {
  light: theme,
  dark: {
    ...theme,
    background: '#1a1a1a',
    backgroundSecondary: '#2d2d2d',
    backgroundTertiary: '#3d3d3d',
    text: '#ffffff',
    textSecondary: '#b3b3b3',
    textTertiary: '#808080',
    cardBackground: '#2d2d2d',
    border: '#404040',
    borderLight: '#3d3d3d',
  },
};

// Helper functions for theme usage
export const getThemeColor = (colorPath) => {
  return colorPath.split('.').reduce((obj, key) => obj?.[key], theme);
};

export const getSpacing = (size) => {
  return theme.spacing[size] || size;
};

export const getShadow = (size) => {
  return theme.shadows[size] || 'none';
};

export default theme;
